import { useState } from 'react'
import { PRODUCT_NAME } from '../brand.js'
import { signIn } from './api.js'

/**
 * Sign In, the two-factor challenge, and the locked-out state.
 *
 * All three live in one component because they are one flow, not three pages:
 * the backend's two-step login means the 2FA challenge is a continuation of the
 * same submission, and lockout is a response to it. Splitting them across routes
 * would mean carrying the password between routes, which is worse.
 *
 * TITLE CASE throughout. No em dashes in any string.
 */

type Props = {
  /** Where to send the user after signing in. Used by the session-expired flow. */
  returnTo?: string
  onSignedIn: (returnTo?: string) => void
  onForgotPassword: () => void
}

type Stage = 'credentials' | 'totp' | 'lockedOut'

function formatDuration(seconds: number): string {
  const mins = Math.ceil(seconds / 60)
  if (mins < 60) return `${mins} ${mins === 1 ? 'Minute' : 'Minutes'}`
  const hours = Math.ceil(mins / 60)
  return `${hours} ${hours === 1 ? 'Hour' : 'Hours'}`
}

export function SignIn({ returnTo, onSignedIn, onForgotPassword }: Props): React.JSX.Element {
  const [stage, setStage] = useState<Stage>('credentials')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [lockoutSeconds, setLockoutSeconds] = useState(0)
  const [busy, setBusy] = useState(false)

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    setBusy(true)
    setError(null)

    // The password is re-sent with the code deliberately. The backend issues no
    // partial session between the two steps, so there is no half-authenticated
    // credential to steal; the cost is re-verifying the password, which is the
    // right trade.
    const result = await signIn(email, password, stage === 'totp' ? totpCode : undefined)
    setBusy(false)

    switch (result.kind) {
      case 'success':
        onSignedIn(returnTo)
        return

      case 'totpRequired':
        setStage('totp')
        setError(null)
        return

      case 'invalidCredentials':
        /**
         * ONE MESSAGE FOR EVERY FAILURE. The backend returns an identical 401
         * for a wrong password, an unknown email and a disabled account, and
         * even equalises the timing. Saying "No Account Found" here would hand
         * back the user-enumeration oracle it closed.
         *
         * On the 2FA step the same message covers a wrong code, which is
         * correct: by then the password is already known good, so the only
         * thing being reported is that this attempt failed.
         */
        setError(
          stage === 'totp'
            ? 'That Code Is Not Correct. Check Your Authenticator App And Try Again.'
            : 'Those Details Are Not Correct.',
        )
        setTotpCode('')
        return

      case 'rateLimited':
        setLockoutSeconds(result.retryAfterSeconds)
        setStage('lockedOut')
        return

      case 'networkError':
        setError('Could Not Reach The Server. Check Your Connection And Try Again.')
        return
    }
  }

  if (stage === 'lockedOut') {
    return (
      <div className="auth">
        <div className="auth__card">
          <p className="auth__brand">{PRODUCT_NAME}</p>
          <h1 className="auth__title">Too Many Attempts</h1>

          <div className="auth__message auth__message--warning">
            <p className="auth__message-title">Sign In Is Temporarily Blocked</p>
            {/*
              Deliberately says nothing about WHICH account, whether it exists,
              or whether the password was ever right. The backend locks per IP
              AND per account; revealing which one tripped would confirm an
              email exists.
            */}
            <p>
              Too many sign in attempts have been made. Please wait about{' '}
              {formatDuration(lockoutSeconds)} and try again.
            </p>
            <p>
              If you have forgotten your password, ask an administrator to reset it for
              you.
            </p>
          </div>

          <button
            type="button"
            className="auth__button auth__button--secondary"
            onClick={() => {
              setStage('credentials')
              setPassword('')
              setTotpCode('')
              setError(null)
            }}
          >
            Back To Sign In
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="auth">
      <div className="auth__card">
        <p className="auth__brand">{PRODUCT_NAME}</p>

        {stage === 'credentials' ? (
          <>
            <h1 className="auth__title">Sign In</h1>
            <p className="auth__subtitle">Enter your details to continue.</p>
          </>
        ) : (
          <>
            <h1 className="auth__title">Two Factor Authentication</h1>
            <p className="auth__subtitle">
              Enter the six digit code from your authenticator app.
            </p>
          </>
        )}

        {error !== null && (
          <div className="auth__message auth__message--error" role="alert">
            <p>{error}</p>
          </div>
        )}

        <form onSubmit={submit}>
          {stage === 'credentials' ? (
            <>
              <div className="auth__field">
                <label className="auth__label" htmlFor="email">
                  Email Address
                </label>
                <input
                  id="email"
                  className="auth__input"
                  type="email"
                  autoComplete="username"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="auth__field">
                <label className="auth__label" htmlFor="password">
                  Password
                </label>
                <input
                  id="password"
                  className="auth__input"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </>
          ) : (
            <div className="auth__field">
              <label className="auth__label" htmlFor="code">
                Authentication Code
              </label>
              <input
                id="code"
                className="auth__input auth__input--code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                required
                autoFocus
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
              />
            </div>
          )}

          <button type="submit" className="auth__button" disabled={busy}>
            {busy ? 'Please Wait' : stage === 'credentials' ? 'Sign In' : 'Verify Code'}
          </button>
        </form>

        {stage === 'credentials' ? (
          <div className="auth__links">
            <button type="button" className="auth__link" onClick={onForgotPassword}>
              Forgot Password
            </button>
          </div>
        ) : (
          <div className="auth__links">
            <button
              type="button"
              className="auth__link"
              onClick={() => {
                setStage('credentials')
                setTotpCode('')
                setError(null)
              }}
            >
              Back To Sign In
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
